import type { ComponentType } from "react"
import { useMemo, useState } from "react"
import {
	IconCheckOutline18 as Check,
	IconAtom2Outline18,
	IconBookOpenOutline18,
	IconCalendarClockOutline18,
	IconChartBarOutline18,
	IconChatBubbleOutline18,
	IconCircleDollarOutline18,
	IconCreditCardOutline18,
	IconGridLayoutOutline18,
	IconStackPerspectiveOutline18,
	IconRepeatOutline18,
	IconTagOutline18,
	IconUserOutline18,
	IconUsersOutline18,
} from "nucleo-ui-outline-18"

import type { NestedCommandGroup } from "@productlane/ui/src/components/internal/nested-command"
import { cn } from "@productlane/lib/src/cn"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import type {
	FilterChip,
	NumericFilter,
	SearchableFilterItem,
} from "@/components/filters"
import {
	FilterControlChips,
	FilterPopover,
	NumericComparisonFilterItems,
	useFilteredSearchItems,
} from "@/components/filters"
import { FEATURE_ICONS, FEATURE_ORDER } from "./feature-catalog"

type IconComp = ComponentType<{ className?: string }>

export interface CustomerRow {
	stripeSubscriptionId: string | null
	stripePriceId: string | null
	hasBeenSetup: boolean
	hasCustomDomain: boolean
	emailConnected: boolean
	slackConnected: boolean
	linearConnected: boolean
	mrr: number | null
	memberCount: number
	contactCount: number
	threadCount: number
	changelogCount: number
	docCount: number
	features: string[]
	lastAiResolutionAt: Date | string | null
	lastThreadAt: Date | string | null
	createdAt: Date | string
	createdBy: { id: string; name: string | null; email: string } | null
}

export interface CustomerFilters {
	features: string[]
	subscription: string[]
	tier: string[]
	stage: string[]
	owners: string[]
	mrr: NumericFilter | null
	activation: NumericFilter | null
	size: NumericFilter | null
	contacts: NumericFilter | null
	threads: NumericFilter | null
	changelogs: NumericFilter | null
	docs: NumericFilter | null
	created: NumericFilter | null
	lastAi: NumericFilter | null
	lastThread: NumericFilter | null
}

export const EMPTY_CUSTOMER_FILTERS: CustomerFilters = {
	features: [],
	subscription: [],
	tier: [],
	stage: [],
	owners: [],
	mrr: null,
	activation: null,
	size: null,
	contacts: null,
	threads: null,
	changelogs: null,
	docs: null,
	created: null,
	lastAi: null,
	lastThread: null,
}

type MultiCat = "Feature" | "Subscription" | "Tier" | "Stage" | "Owner"
type NumCat =
	| "MRR"
	| "Activation"
	| "Size"
	| "Contacts"
	| "Threads"
	| "Changelogs"
	| "Docs"
	| "Created"
	| "LastAi"
	| "LastThread"
type Cat = MultiCat | NumCat

const MULTI_FIELD = {
	Feature: "features",
	Subscription: "subscription",
	Tier: "tier",
	Stage: "stage",
	Owner: "owners",
} as const satisfies Record<MultiCat, keyof CustomerFilters>

const NUM_FIELD = {
	MRR: "mrr",
	Activation: "activation",
	Size: "size",
	Contacts: "contacts",
	Threads: "threads",
	Changelogs: "changelogs",
	Docs: "docs",
	Created: "created",
	LastAi: "lastAi",
	LastThread: "lastThread",
} as const satisfies Record<NumCat, keyof CustomerFilters>

const MULTI_CATS = Object.keys(MULTI_FIELD) as MultiCat[]
const DATE_CATS = new Set<Cat>(["Created", "LastAi", "LastThread"])

const CATEGORY_ORDER: Cat[] = [
	"Feature",
	"Subscription",
	"Tier",
	"Stage",
	"Owner",
	"MRR",
	"Activation",
	"Size",
	"Contacts",
	"Threads",
	"Changelogs",
	"Docs",
	"Created",
	"LastAi",
	"LastThread",
]

const CAT_LABEL: Record<Cat, string> = {
	Feature: "Feature",
	Subscription: "Subscription",
	Tier: "Tier",
	Stage: "Stage",
	Owner: "Owner",
	MRR: "MRR",
	Activation: "Activation",
	Size: "Members",
	Contacts: "Contacts",
	Threads: "Threads",
	Changelogs: "Changelog",
	Docs: "Help center",
	Created: "Created",
	LastAi: "Last AI",
	LastThread: "Last thread",
}

const CAT_ICON: Record<Cat, IconComp> = {
	Feature: IconGridLayoutOutline18,
	Subscription: IconCreditCardOutline18,
	Tier: IconTagOutline18,
	Stage: IconRepeatOutline18,
	Owner: IconUserOutline18,
	MRR: IconCircleDollarOutline18,
	Activation: IconChartBarOutline18,
	Size: IconUsersOutline18,
	Contacts: IconUserOutline18,
	Threads: IconChatBubbleOutline18,
	Changelogs: IconStackPerspectiveOutline18,
	Docs: IconBookOpenOutline18,
	Created: IconCalendarClockOutline18,
	LastAi: IconAtom2Outline18,
	LastThread: IconCalendarClockOutline18,
}

const NUM_FORMAT: Partial<Record<Cat, { prefix?: string; suffix?: string }>> = {
	MRR: { prefix: "€" },
	Created: { suffix: "days ago" },
	LastAi: { suffix: "days ago" },
	LastThread: { suffix: "days ago" },
}

const CATEGORIES: NestedCommandGroup<Cat>[] = CATEGORY_ORDER.map((type) => ({
	type,
	label: CAT_LABEL[type],
	Icon: CAT_ICON[type] as NestedCommandGroup<Cat>["Icon"],
}))

const DAY_MS = 24 * 60 * 60 * 1000

function ageDays(value: Date | string | null): number {
	if (!value) return Infinity
	return (Date.now() - new Date(value).getTime()) / DAY_MS
}

function activationScore(w: CustomerRow): number {
	return (
		Number(w.hasCustomDomain) +
		Number(w.emailConnected) +
		Number(w.slackConnected) +
		Number(w.linearConnected)
	)
}

function classify(cat: MultiCat, w: CustomerRow): string {
	switch (cat) {
		case "Subscription":
			return w.stripeSubscriptionId ? "Active" : "Inactive"
		case "Tier":
			return w.stripePriceId ? "Paid" : "Free"
		case "Stage":
			return w.stripeSubscriptionId ? "Active" : w.hasBeenSetup ? "Free" : "New"
		default:
			return ""
	}
}

function matchNumeric(value: number, f: NumericFilter): boolean {
	switch (f.operator) {
		case ">=":
			return value >= f.value
		case "<=":
			return value <= f.value
		case "=":
			return value === f.value
		case "!=":
			return value !== f.value
	}
}

export function applyCustomerFilters<T extends CustomerRow>(
	rows: T[],
	f: CustomerFilters,
): T[] {
	return rows.filter((w) => {
		if (f.features.length && !f.features.every((x) => w.features.includes(x)))
			return false
		if (
			f.subscription.length &&
			!f.subscription.includes(classify("Subscription", w))
		)
			return false
		if (f.tier.length && !f.tier.includes(classify("Tier", w))) return false
		if (f.stage.length && !f.stage.includes(classify("Stage", w))) return false
		if (f.owners.length && !f.owners.includes(w.createdBy?.id ?? "none"))
			return false
		if (f.mrr && !matchNumeric(w.mrr ?? 0, f.mrr)) return false
		if (f.activation && !matchNumeric(activationScore(w), f.activation))
			return false
		if (f.size && !matchNumeric(w.memberCount, f.size)) return false
		if (f.contacts && !matchNumeric(w.contactCount, f.contacts)) return false
		if (f.threads && !matchNumeric(w.threadCount, f.threads)) return false
		if (f.changelogs && !matchNumeric(w.changelogCount, f.changelogs))
			return false
		if (f.docs && !matchNumeric(w.docCount, f.docs)) return false
		if (f.created && !matchNumeric(ageDays(w.createdAt), f.created))
			return false
		if (f.lastAi && !matchNumeric(ageDays(w.lastAiResolutionAt), f.lastAi))
			return false
		if (f.lastThread && !matchNumeric(ageDays(w.lastThreadAt), f.lastThread))
			return false
		return true
	})
}

interface Option {
	value: string
	label: string
	Icon?: IconComp
	count: number
}

const STATIC_OPTIONS: Partial<Record<Cat, { value: string; label: string }[]>> =
	{
		Subscription: [
			{ value: "Active", label: "Active" },
			{ value: "Inactive", label: "Inactive" },
		],
		Tier: [
			{ value: "Paid", label: "Paid" },
			{ value: "Free", label: "Free" },
		],
		Stage: [
			{ value: "Active", label: "Active" },
			{ value: "Free", label: "Free" },
			{ value: "New", label: "New" },
		],
	}

function computeOptions(cat: Cat, rows: CustomerRow[]): Option[] {
	if (cat === "Feature") {
		return FEATURE_ORDER.map((name) => ({
			value: name,
			label: name,
			Icon: FEATURE_ICONS[name],
			count: rows.reduce((n, r) => n + (r.features.includes(name) ? 1 : 0), 0),
		}))
	}
	if (cat === "Owner") {
		const byId = new Map<string, { label: string; count: number }>()
		for (const r of rows) {
			const id = r.createdBy?.id ?? "none"
			const label = r.createdBy?.email ?? "No owner"
			const existing = byId.get(id) ?? { label, count: 0 }
			existing.count++
			byId.set(id, existing)
		}
		return Array.from(byId, ([value, { label, count }]) => ({
			value,
			label,
			count,
		})).sort((a, b) => b.count - a.count)
	}
	const opts = STATIC_OPTIONS[cat]
	if (!opts) return []
	return opts.map((o) => ({
		...o,
		count: rows.reduce(
			(n, r) => n + (classify(cat as MultiCat, r) === o.value ? 1 : 0),
			0,
		),
	}))
}

function isNumCat(cat: Cat): cat is NumCat {
	return cat in NUM_FIELD
}

function setField(
	filters: CustomerFilters,
	key: keyof CustomerFilters,
	value: string[] | NumericFilter | null,
): CustomerFilters {
	return { ...filters, [key]: value } as CustomerFilters
}

function CategoryPanel({
	cat,
	filters,
	onChange,
	rows,
	onAfterSelect,
}: {
	cat: Cat
	filters: CustomerFilters
	onChange: (next: CustomerFilters) => void
	rows: CustomerRow[]
	onAfterSelect?: () => void
}) {
	const [query, setQuery] = useState("")
	const options = useMemo(() => computeOptions(cat, rows), [cat, rows])

	if (isNumCat(cat)) {
		const field = NUM_FIELD[cat]
		const value = filters[field]
		const fmt = NUM_FORMAT[cat]
		return (
			<Command shouldFilter={false}>
				<CommandList className="max-h-[min(70dvh,320px)]">
					<CommandGroup>
						<NumericComparisonFilterItems
							value={value}
							onChange={(v) => onChange(setField(filters, field, v))}
							onSelect={() => onAfterSelect?.()}
							placeholder={
								DATE_CATS.has(cat) ? "Days ago..." : "Enter a number..."
							}
							prefix={fmt?.prefix}
							suffix={fmt?.suffix}
						/>
					</CommandGroup>
				</CommandList>
			</Command>
		)
	}

	const field = MULTI_FIELD[cat]
	const selected = filters[field]
	const toggle = (v: string) => {
		const next = selected.includes(v)
			? selected.filter((x) => x !== v)
			: [...selected, v]
		onChange(setField(filters, field, next))
		onAfterSelect?.()
	}

	return (
		<Command>
			<CommandInput
				autoFocus
				placeholder="Filter..."
				value={query}
				onValueChange={setQuery}
			/>
			<CommandList className="max-h-[min(70dvh,320px)]">
				<CommandEmpty>No results found.</CommandEmpty>
				<CommandGroup>
					{options.map((o) => (
						<CommandItem
							key={o.value}
							value={o.label}
							onSelect={() => toggle(o.value)}
						>
							<div className="flex w-full items-center gap-2">
								{o.Icon ? (
									<o.Icon className="h-4 w-4 shrink-0 text-secondary" />
								) : null}
								<span className="flex-1 truncate">{o.label}</span>
								<span className="tabular-nums text-tertiary">{o.count}</span>
								<Check
									className={cn("h-4 w-4 shrink-0", {
										"opacity-100": selected.includes(o.value),
										"opacity-0": !selected.includes(o.value),
									})}
								/>
							</div>
						</CommandItem>
					))}
				</CommandGroup>
			</CommandList>
		</Command>
	)
}

export function CustomersFilterPopover({
	filters,
	onChange,
	rows,
}: {
	filters: CustomerFilters
	onChange: (next: CustomerFilters) => void
	rows: CustomerRow[]
}) {
	const [globalQuery, setGlobalQuery] = useState("")

	const counts = useMemo(() => {
		const c = {} as Record<Cat, number>
		for (const cat of CATEGORY_ORDER) {
			c[cat] = isNumCat(cat)
				? filters[NUM_FIELD[cat]]
					? 1
					: 0
				: filters[MULTI_FIELD[cat]].length
		}
		return c
	}, [filters])

	const searchableItems = useMemo<SearchableFilterItem<Cat>[]>(() => {
		const items: SearchableFilterItem<Cat>[] = []
		for (const cat of MULTI_CATS) {
			const field = MULTI_FIELD[cat]
			const selected = filters[field]
			for (const o of computeOptions(cat, rows)) {
				items.push({
					id: `${cat}-${o.value}`,
					category: cat,
					categoryLabel: CAT_LABEL[cat],
					label: o.label,
					searchText: o.label,
					Icon: o.Icon as SearchableFilterItem<Cat>["Icon"],
					onSelect: () => {
						const next = selected.includes(o.value)
							? selected.filter((x) => x !== o.value)
							: [...selected, o.value]
						onChange(setField(filters, field, next))
					},
					isSelected: selected.includes(o.value),
				})
			}
		}
		return items
	}, [filters, rows, onChange])

	const filteredSearchItems = useFilteredSearchItems(
		globalQuery,
		searchableItems,
	)

	return (
		<FilterPopover<Cat>
			categories={CATEGORIES}
			counts={counts}
			renderSubPanel={(group, _q, _setQ, onFilterItemSelect) => (
				<CategoryPanel
					cat={group}
					filters={filters}
					onChange={onChange}
					rows={rows}
					onAfterSelect={onFilterItemSelect}
				/>
			)}
			searchableItems={searchableItems}
			filteredSearchItems={filteredSearchItems}
			onGlobalQueryChange={setGlobalQuery}
			subPanelWidthClassName={(group) => (group === "Owner" ? "w-72" : "w-64")}
			tooltipLabel="Filter customers"
		/>
	)
}

function numericChipValue(cat: Cat, f: NumericFilter): string {
	const fmt = NUM_FORMAT[cat]
	return `${f.operator} ${fmt?.prefix ?? ""}${f.value}${fmt?.suffix ? ` ${fmt.suffix}` : ""}`
}

export function CustomersFilterChips({
	filters,
	onChange,
	rows,
}: {
	filters: CustomerFilters
	onChange: (next: CustomerFilters) => void
	rows: CustomerRow[]
}) {
	const chips = useMemo<FilterChip[]>(() => {
		const result: FilterChip[] = []
		for (const cat of CATEGORY_ORDER) {
			const popoverContent = (
				<CategoryPanel
					cat={cat}
					filters={filters}
					onChange={onChange}
					rows={rows}
				/>
			)
			if (isNumCat(cat)) {
				const field = NUM_FIELD[cat]
				const value = filters[field]
				if (!value) continue
				result.push({
					key: cat,
					label: CAT_LABEL[cat],
					middleText: "is",
					valueText: numericChipValue(cat, value),
					onClear: () => onChange(setField(filters, field, null)),
					popoverContent,
				})
			} else {
				const field = MULTI_FIELD[cat]
				const selected = filters[field]
				if (selected.length === 0) continue
				const single =
					selected.length === 1
						? (computeOptions(cat, rows).find((o) => o.value === selected[0])
								?.label ?? selected[0]!)
						: `${selected.length} selected`
				result.push({
					key: cat,
					label: CAT_LABEL[cat],
					middleText: selected.length > 1 ? "are" : "is",
					valueText: single,
					onClear: () => onChange(setField(filters, field, [])),
					popoverContent,
				})
			}
		}
		return result
	}, [filters, rows, onChange])

	return <FilterControlChips chips={chips} />
}
